import type { AudioBackend, PlayRequest } from './sfx';

/**
 * WebAudio implementation of {@link AudioBackend} (spec.md §N task §7).
 *
 * Autoplay-safe: no AudioContext is created until {@link WebAudioBackend.unlock} runs
 * inside a user gesture, and `ready()` stays false until the context is running — so
 * the {@link SfxEngine} silently skips sounds before the first interaction.
 *
 * Graceful fallback: when a request has no file (or its file has failed to decode) a
 * short synthesized tone is played from the manifest's `tone` spec. Since this phase
 * ships no audio binaries, every key tone-falls-back today; dropping real `.ogg`/`.wav`
 * files next to the manifest makes them decode and play with **no code change**.
 */
export class WebAudioBackend implements AudioBackend {
  private ctx: AudioContext | null = null;
  private unlocked = false;
  private readonly buffers = new Map<string, AudioBuffer | 'failed' | 'loading'>();

  /** True once audio may play (context exists and is running). */
  ready(): boolean {
    return this.unlocked && this.ctx !== null && this.ctx.state === 'running';
  }

  /** Create/resume the AudioContext from within a user gesture. Idempotent. */
  async unlock(): Promise<void> {
    try {
      if (!this.ctx) {
        const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return;
        this.ctx = new Ctor();
      }
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      this.unlocked = true;
    } catch {
      /* audio unavailable — stay locked, never throw */
    }
  }

  play(req: PlayRequest): void {
    const ctx = this.ctx;
    if (!ctx || !this.ready()) return;
    if (req.file) {
      const cached = this.buffers.get(req.file);
      if (cached instanceof AudioBuffer) {
        this.playBuffer(ctx, cached, req);
        return;
      }
      if (cached === undefined) void this.loadBuffer(req.file); // kick off decode for next time
      // Until decoded (or if it has failed), fall back to a tone so there is feedback.
    }
    if (req.tone) this.playTone(ctx, req);
  }

  private async loadBuffer(url: string): Promise<void> {
    if (this.buffers.has(url) || !this.ctx) return;
    this.buffers.set(url, 'loading');
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.arrayBuffer();
      const buf = await this.ctx.decodeAudioData(data);
      this.buffers.set(url, buf);
    } catch {
      this.buffers.set(url, 'failed'); // permanent tone fallback for this file
    }
  }

  private playBuffer(ctx: AudioContext, buffer: AudioBuffer, req: PlayRequest): void {
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = req.pitch;
    const gain = ctx.createGain();
    gain.gain.value = req.volume;
    src.connect(gain).connect(ctx.destination);
    src.start();
  }

  private playTone(ctx: AudioContext, req: PlayRequest): void {
    const tone = req.tone!;
    const now = ctx.currentTime;
    const durSec = tone.durationMs / 1000;
    const osc = ctx.createOscillator();
    osc.type = tone.type ?? 'sine';
    const baseFreq = tone.freq * req.pitch;
    osc.frequency.setValueAtTime(baseFreq, now);
    if (tone.sweepTo) osc.frequency.linearRampToValueAtTime(tone.sweepTo * req.pitch, now + durSec);

    const gain = ctx.createGain();
    // Quick attack + exponential-ish decay so tones read as "blips", not beeps.
    const peak = Math.max(0.0001, req.volume);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + durSec);

    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + durSec + 0.02);
  }
}
